import React from 'react';
import { cn } from '@/lib/cn';
import { formatRupees } from '@/lib/format';
import { SERVICE_CATEGORY_LABELS, type ServiceSummary, type Uuid } from '@shared/types/api';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

interface ServicesTableProps {
  services: ServiceSummary[];
  isLoading: boolean;
  statusFilter: 'ACTIVE' | 'INACTIVE';
  draggedIndex: number | null;
  onDragStart: (e: React.DragEvent<HTMLTableRowElement>, index: number) => void;
  onDragOver: (e: React.DragEvent<HTMLTableRowElement>, index: number) => void;
  onDragEnd: () => void;
  onEdit: (service: ServiceSummary) => void;
  onActivate: (id: Uuid) => void;
  isActivating: boolean;
  onDeactivate: (id: Uuid) => void;
  isDeactivating: boolean;
}

export function ServicesTable({
  services,
  isLoading,
  statusFilter,
  draggedIndex,
  onDragStart,
  onDragOver,
  onDragEnd,
  onEdit,
  onActivate,
  isActivating,
  onDeactivate,
  isDeactivating,
}: ServicesTableProps) {
  const { canEdit, canDelete } = useSectionPermission('SERVICES');
  const colSpan = (canEdit ? 1 : 0) + 8;

  return (
    <section className="mt-5 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[850px] text-sm text-left">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <tr>
              {canEdit && <th className="w-10 px-4 py-3 whitespace-nowrap" />}
              <th className="px-4 py-3 font-medium whitespace-nowrap">Name</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Category</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Sub-category</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Starts at</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">GST</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">HSN</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Active</th>
              <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={colSpan} className="p-6 text-center text-gray-500">Loading…</td></tr>
            ) : services.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="p-6 text-center text-gray-500">
                  {statusFilter === 'ACTIVE'
                    ? 'No active services match the selected category.'
                    : 'No deactivated services.'}
                </td>
              </tr>
            ) : services.map((s, idx) => {
              const startsAt =
                Number(s.defaultAssociateFeeRupees ?? s.defaultApplicationFeeRupees ?? 0) +
                Number(s.defaultPlatformFeeRupees ?? s.defaultServiceChargeRupees ?? 0);
              return (
                <tr
                  key={s.id}
                  className={cn(
                    'hover:bg-gray-50 transition-colors duration-150',
                    canEdit && 'cursor-grab active:cursor-grabbing',
                    draggedIndex === idx && 'bg-brand-50/50 opacity-50 border-y-2 border-brand-200',
                    !s.isActive && 'bg-gray-50/60 opacity-80'
                  )}
                  draggable={canEdit}
                  onDragStart={(e) => canEdit && onDragStart(e, idx)}
                  onDragOver={(e) => canEdit && onDragOver(e, idx)}
                  onDragEnd={canEdit ? onDragEnd : undefined}
                >
                  {canEdit && (
                    <td className="px-4 py-3 text-gray-400 select-none w-10 whitespace-nowrap">
                      <svg
                        className="w-4 h-4 opacity-40 hover:opacity-80 transition-opacity"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="9" cy="5" r="2" />
                        <circle cx="9" cy="12" r="2" />
                        <circle cx="9" cy="19" r="2" />
                        <circle cx="15" cy="5" r="2" />
                        <circle cx="15" cy="12" r="2" />
                        <circle cx="15" cy="19" r="2" />
                      </svg>
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{s.name}</div>
                    {s.description ? <div className="max-w-xs truncate text-xs text-gray-500">{s.description}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{SERVICE_CATEGORY_LABELS[s.category]}</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{s.subCategory ?? <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {s.isStartsFrom
                      ? `Starts from ${formatRupees(Number(s.startsFromRupees ?? 0))}`
                      : formatRupees(startsAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{s.gstPercentage}%</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{s.associateHsnSacCode ?? (s as any).hsnSacCode ?? '998213'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', s.isActive ? 'bg-brand-50 text-brand-800' : 'bg-red-50 text-red-700 border border-red-200')}>
                      {s.isActive ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap pr-6">
                    <div className="flex justify-end gap-2 text-xs">
                      {canEdit ? (
                        <button onClick={() => onEdit(s)} className="text-brand-700 hover:underline font-medium">Edit</button>
                      ) : (
                        <button
                          onClick={() => onEdit(s)}
                          className="inline-flex items-center rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                        >
                          View
                        </button>
                      )}
                      {canDelete && (
                        s.isActive ? (
                          <button
                            onClick={() => onDeactivate(s.id as Uuid)}
                            className="text-red-600 hover:underline font-medium"
                            disabled={isDeactivating}
                          >
                            {isDeactivating ? 'Deactivating…' : 'Deactivate'}
                          </button>
                        ) : (
                          <button
                            onClick={() => onActivate(s.id as Uuid)}
                            className="text-emerald-700 hover:underline font-semibold"
                            disabled={isActivating}
                          >
                            {isActivating ? 'Activating…' : 'Activate'}
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
